import type { MarketplaceFulfillmentStatus } from "@nexus/contracts";

import type { MarketplaceStatusBadgeVariant } from "./fulfillment-badge-variant";
import {
  countFilledScriptVideos,
  videoFulfillmentStatusChips,
} from "./video-display-status";
import type {
  OrderLineVideoReviewStatus,
  OrderScriptRow,
} from "../types/marketplace.types";

/** Terminal line statuses — never split into per-video chips. */
const LINE_LEVEL_FULFILLMENT_STATUSES = new Set<MarketplaceFulfillmentStatus>([
  "ORDER_DELIVERED",
  "CANCELLED",
  "COMPLETED_PRE_ADGE",
]);

export const VIDEO_REVIEW_STATUSES = [
  "IN_REVIEW",
  "REVISION_REQUESTED",
] as const satisfies readonly OrderLineVideoReviewStatus[];

type VideoStatusSource = Pick<
  OrderScriptRow,
  "fulfillmentStatus" | "videos" | "videoStatusCounts"
>;

export function usesVideoStatusChips(script: VideoStatusSource): boolean {
  if (LINE_LEVEL_FULFILLMENT_STATUSES.has(script.fulfillmentStatus)) {
    return false;
  }
  if (countFilledScriptVideos(script) <= 1) {
    return false;
  }
  return videoFulfillmentStatusChips(script).length > 0;
}

/** @deprecated Prefer `videoFulfillmentStatusChips` — counts use script fulfillment labels. */
export function videoStatusChips(
  script: Pick<OrderScriptRow, "fulfillmentStatus" | "videos" | "videoStatusCounts">,
): { status: OrderLineVideoReviewStatus; count: number }[] {
  return videoFulfillmentStatusChips(script).map((chip) => ({
    status:
      chip.status === "SENT_FOR_REVISION"
        ? "REVISION_REQUESTED"
        : "IN_REVIEW",
    count: chip.count,
  }));
}

/** Hue from the existing fulfillment badge tokens. Approved arrives in a later deliverable. */
export function videoReviewBadgeVariant(
  status: OrderLineVideoReviewStatus,
): MarketplaceStatusBadgeVariant {
  if (status === "REVISION_REQUESTED") return "status-error";
  return "status-sent";
}
